import { useEffect, useMemo, useState } from "react"
import type {
  CarriageLabelChanges,
  CarriageLabelKey,
  CarriageLabelOverrideOp,
  CarriageLabelRow,
} from "./carriageLabelsTypes"
import { CarriageLabel } from "./templates/CarriageLabel"
import { buildCarriageLabelsPdf } from "./carriageLabelPdf"
import { downloadPdf } from "./pdfBuilder"
import { saveCarriageLabelOverrides } from "./useCarriageLabels"

// Label editor — opened from a search result on the Carriage Labels tab.
//
// The selection (every label the search matched) is listed down the left; the
// active one is edited on the right. The "Apply to" pills decide whether a field
// edit lands on the whole selection or just the active label, defaulting to the
// whole selection when there is more than one.
//
// Apply persists to cache.CarriageLabelOverrides, which is separate from the
// nightly-truncated cache, so edits survive the ONTRACK refresh.

export interface CarriageLabelEditorDialogProps {
  open: boolean
  rows: CarriageLabelRow[]
  /** Index of the result the user clicked. */
  initialIndex: number
  onClose: () => void
  /** Called after a successful save so the list behind can refetch. */
  onSaved: () => void
}

type ApplyMode = "all" | "one"

/** The editable fields, as the form holds them (all strings). */
interface Fields {
  banner: string
  account: string
  route: string
  time: string
  date: string
  qty: string
  booking: string
}

const FIELD_LABELS: { key: keyof Fields; label: string; placeholder: string }[] = [
  { key: "banner", label: "Banner", placeholder: "RESERVED" },
  { key: "account", label: "Group or name", placeholder: "Leger Shearings" },
  { key: "route", label: "Route", placeholder: "Pickering to Whitby" },
  { key: "time", label: "Departure time", placeholder: "10:55" },
  { key: "date", label: "Date", placeholder: "Tuesday, 28 July 2026" },
  { key: "qty", label: "Party size", placeholder: "48" },
  { key: "booking", label: "Booking reference", placeholder: "273699" },
]

function keyOf(r: CarriageLabelRow): string {
  return `${r.BookingKey ?? ""}|${r.JourneyDate ?? ""}|${r.TimeOutKey ?? ""}|${r.LabelNo ?? ""}`
}

/** Rows that carry a full MERAC identity can be saved; anything else is print-only. */
function labelKey(r: CarriageLabelRow): CarriageLabelKey | null {
  if (r.BookingKey == null || !r.JourneyDate || !r.TimeOutKey || r.LabelNo == null) return null
  return { bookingKey: r.BookingKey, journeyDate: r.JourneyDate, timeOutKey: r.TimeOutKey, labelNo: r.LabelNo }
}

function fieldsOf(r: CarriageLabelRow): Fields {
  return {
    banner: r.BannerText ?? "",
    account: r.Account ?? "",
    route: (r.OutFrom ?? "").replace(/\s+/g, " ").trim(),
    time: r.TimeOut ?? "",
    date: r.JourneyDayDate ?? "",
    qty: r.Qty == null ? "" : String(r.Qty),
    booking: r.Booking == null ? "" : String(r.Booking),
  }
}

/** Turn the edited fields back into a row the template can render. */
function rowWith(base: CarriageLabelRow, f: Fields): CarriageLabelRow {
  const qtyNum = Number(f.qty)
  return {
    ...base,
    BannerText: f.banner.trim() || null,
    Account: f.account,
    OutFrom: f.route,
    TimeOut: f.time,
    JourneyDayDate: f.date,
    Qty: f.qty.trim() !== "" && Number.isFinite(qtyNum) ? qtyNum : null,
    Booking: f.booking.trim() !== "" ? f.booking.trim() : null,
  }
}

const CHANGE_KEY: Record<keyof Fields, keyof CarriageLabelChanges> = {
  banner: "bannerText",
  account: "account",
  route: "outFrom",
  time: "timeOut",
  date: "journeyDayDate",
  qty: "qty",
  booking: "bookingText",
}

export function CarriageLabelEditorDialog({ open, rows, initialIndex, onClose, onSaved }: CarriageLabelEditorDialogProps) {
  const [active, setActive] = useState(0)
  const [mode, setMode] = useState<ApplyMode>("all")
  // Fields edited for the whole selection, and per-label edits keyed by identity.
  const [bulk, setBulk] = useState<Partial<Fields>>({})
  const [perLabel, setPerLabel] = useState<Record<string, Partial<Fields>>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null)

  useEffect(() => {
    if (!open) return
    setActive(Math.max(0, Math.min(initialIndex, rows.length - 1)))
    setMode(rows.length > 1 ? "all" : "one")
    setBulk({})
    setPerLabel({})
    setBusy(false)
    setError(null)
    setNotice(null)
    setProgress(null)
  }, [open, initialIndex, rows.length])

  // Escape closes this dialog only, not the A La Carte modal behind it.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose() } }
    document.addEventListener("keydown", onKey, true)
    return () => document.removeEventListener("keydown", onKey, true)
  }, [open, onClose])

  /** Effective fields for a row: per-label edit wins, then bulk, then MERAC. */
  const effective = useMemo(() => {
    return rows.map((r) => {
      const base = fieldsOf(r)
      const mine = perLabel[keyOf(r)] ?? {}
      const out = { ...base }
      for (const { key } of FIELD_LABELS) {
        const v = mine[key] ?? bulk[key]
        if (v !== undefined) out[key] = v
      }
      return out
    })
  }, [rows, bulk, perLabel])

  const editedRows = useMemo(() => rows.map((r, i) => rowWith(r, effective[i]!)), [rows, effective])

  const dirtyKeys = useMemo(() => {
    const set = new Set<keyof Fields>()
    for (const { key } of FIELD_LABELS) {
      if (bulk[key] !== undefined) set.add(key)
      for (const p of Object.values(perLabel)) if (p[key] !== undefined) set.add(key)
    }
    return set
  }, [bulk, perLabel])

  const dirtyRowIdx = useMemo(
    () => rows.map((r, i) => {
      const before = fieldsOf(r)
      const after = effective[i]!
      return FIELD_LABELS.some(({ key }) => before[key] !== after[key])
    }),
    [rows, effective]
  )
  const dirtyCount = dirtyRowIdx.filter(Boolean).length

  if (!open || rows.length === 0) return null

  const activeRow = rows[active]!
  const activeFields = effective[active]!
  const unsaveable = rows.filter((r) => labelKey(r) === null).length

  const setField = (k: keyof Fields, v: string) => {
    setNotice(null)
    if (mode === "all") setBulk((prev) => ({ ...prev, [k]: v }))
    else setPerLabel((prev) => ({ ...prev, [keyOf(activeRow)]: { ...(prev[keyOf(activeRow)] ?? {}), [k]: v } }))
  }

  /** Which rows a field edit currently lands on. */
  const targets = (): CarriageLabelRow[] => (mode === "all" ? rows : [activeRow])

  const onApply = async () => {
    if (dirtyKeys.size === 0) { setError("Nothing has been changed yet."); return }
    setBusy(true); setError(null); setNotice(null)
    try {
      const ops: CarriageLabelOverrideOp[] = []

      // One op per row, carrying exactly that row's effective changes. Simpler
      // than trying to reconstruct bulk-vs-per-label server side, and the row
      // count here is bounded by the search cap.
      for (let i = 0; i < rows.length; i++) {
        if (!dirtyRowIdx[i]) continue
        const k = labelKey(rows[i]!)
        if (!k) continue
        const before = fieldsOf(rows[i]!)
        const after = effective[i]!
        const changes: CarriageLabelChanges = {}
        for (const { key } of FIELD_LABELS) {
          if (before[key] === after[key]) continue
          const target = CHANGE_KEY[key]
          if (key === "qty") {
            const n = Number(after.qty)
            changes.qty = after.qty.trim() === "" || !Number.isFinite(n) ? null : Math.trunc(n)
          } else {
            ;(changes as Record<string, unknown>)[target] = after[key].trim() === "" ? null : after[key]
          }
        }
        if (Object.keys(changes).length > 0) ops.push({ labels: [k], changes })
      }

      if (ops.length === 0) { setError("These labels can't be saved (no MERAC identity)."); return }

      const res = await saveCarriageLabelOverrides(ops)
      setBulk({})
      setPerLabel({})
      setNotice(`Saved ${res.saved} label${res.saved === 1 ? "" : "s"}. Edits survive the nightly refresh.`)
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally { setBusy(false) }
  }

  const onReset = async () => {
    const scope = targets()
    const keys = scope.map(labelKey).filter((k): k is CarriageLabelKey => k !== null)
    if (keys.length === 0) { setError("Nothing here can be reset."); return }
    setBusy(true); setError(null); setNotice(null)
    try {
      const res = await saveCarriageLabelOverrides([{ labels: keys, reset: true }])
      setBulk({})
      setPerLabel({})
      setNotice(`Reset ${res.cleared} label${res.cleared === 1 ? "" : "s"} back to MERAC.`)
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally { setBusy(false) }
  }

  // Print what's on screen — the whole selection as edited, or just the active
  // label when the pills are set to this-label-only.
  const onPrint = async () => {
    const toPrint = mode === "all" ? editedRows : [editedRows[active]!]
    setBusy(true); setError(null); setProgress({ current: 0, total: toPrint.length })
    try {
      const res = await buildCarriageLabelsPdf({ rows: toPrint, onProgress: (current, total) => setProgress({ current, total }) })
      downloadPdf(res.blob, `carriage-labels-edited-${toPrint.length}.pdf`)
      if (res.failed.length > 0) setError(`${res.failed.length} label(s) failed to render.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally { setBusy(false); setProgress(null) }
  }

  return (
    <div className="cl-custom-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="cl-custom cl-editor" role="dialog" aria-modal="true" aria-labelledby="cl-editor-title">
        <div className="cl-custom-head">
          <h3 id="cl-editor-title">
            Edit {rows.length === 1 ? "label" : `${rows.length} labels`}
            {dirtyCount > 0 ? ` — ${dirtyCount} changed` : ""}
          </h3>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className={"cl-editor-body" + (rows.length > 1 ? "" : " cl-editor-body--single")}>
          {rows.length > 1 && (
            <aside className="cl-editor-list" aria-label="Labels in this selection">
              {rows.map((r, i) => (
                <button
                  key={keyOf(r) + i}
                  type="button"
                  className={"cl-editor-listitem" + (i === active ? " cl-editor-listitem--active" : "")}
                  onClick={() => setActive(i)}
                >
                  <span className="cl-editor-listitem-name">
                    {effective[i]!.account || "(no name)"}
                    {dirtyRowIdx[i] && <span className="cl-editor-dot" title="Changed, not yet applied">●</span>}
                    {!dirtyRowIdx[i] && r.Edited && <span className="cl-editor-badge" title="Has saved edits">edited</span>}
                  </span>
                  <span className="cl-editor-listitem-meta">
                    {effective[i]!.route} · {effective[i]!.time}
                    {r.LabelNo != null && r.OfLabels != null ? ` · ${r.LabelNo}/${r.OfLabels}` : ""}
                  </span>
                </button>
              ))}
            </aside>
          )}

          <div className="cl-editor-form">
            {FIELD_LABELS.map(({ key, label, placeholder }) => (
              <label className="alacarte-field" key={key}>
                <span>
                  {label}
                  {dirtyKeys.has(key) && <em className="cl-editor-changed"> changed</em>}
                </span>
                <input
                  type="text"
                  value={activeFields[key]}
                  placeholder={placeholder}
                  onChange={(e) => setField(key, e.target.value)}
                  inputMode={key === "qty" ? "numeric" : undefined}
                />
              </label>
            ))}
          </div>

          <div className="cl-custom-preview">
            <div className="cl-custom-preview-label">Preview</div>
            <div className="cl-custom-preview-sheet">
              <CarriageLabel row={editedRows[active]!} style={{ width: "100%", height: "auto" }} />
            </div>
            <p className="cl-custom-preview-note">
              {rows.length > 1
                ? mode === "all"
                  ? `Editing a field changes all ${rows.length} labels in this selection.`
                  : "Editing a field changes only the label selected on the left."
                : "Editing a field changes this label."}
              {unsaveable > 0 ? ` ${unsaveable} label(s) in this selection can't be saved and are print-only.` : ""}
            </p>
          </div>
        </div>

        <footer className="alacarte-footer cl-editor-footer">
          <div className="cl-editor-pills" role="radiogroup" aria-label="Apply changes to">
            <span className="cl-editor-pills-label">Apply to</span>
            <button
              type="button"
              role="radio"
              aria-checked={mode === "all"}
              className={"cl-editor-pill" + (mode === "all" ? " cl-editor-pill--active" : "")}
              onClick={() => setMode("all")}
              disabled={rows.length === 1}
            >
              All {rows.length > 1 ? rows.length : ""} labels
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={mode === "one"}
              className={"cl-editor-pill" + (mode === "one" ? " cl-editor-pill--active" : "")}
              onClick={() => setMode("one")}
            >
              This label only
            </button>
          </div>

          <div className="alacarte-footer-summary">
            {error
              ? <span className="alacarte-footer-error">{error}</span>
              : progress
                ? <span>Rendering {progress.current} / {progress.total}&hellip;</span>
                : notice
                  ? <span className="cl-editor-notice">{notice}</span>
                  : dirtyCount > 0
                    ? <span>{dirtyCount} label{dirtyCount === 1 ? "" : "s"} changed, not yet applied</span>
                    : <span className="alacarte-footer-hint">No changes yet</span>}
          </div>

          <div className="alacarte-footer-actions">
            <button type="button" className="btn btn--ghost" onClick={() => void onReset()} disabled={busy}
              title={mode === "all" ? "Discard saved edits for every label in this selection" : "Discard saved edits for this label"}>
              Reset to MERAC
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => void onPrint()} disabled={busy}>
              Print to PDF
            </button>
            <button type="button" className="btn btn--primary" onClick={() => void onApply()} disabled={busy || dirtyCount === 0}>
              {busy ? "Applying…" : "Apply"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}
